import {
  structuredColumnsControllerGetPartnerColumns,
  structuredColumnsControllerTogglePartnerColumn,
} from '@artic/api-client';
import type {
  PartnerStructuredColumnDto,
  PartnerStructuredColumnToggleDto,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';

export const STRUCTURED_COLUMNS_KEY = 'structured-columns' as const;

/**
 * Per-partner Structured Columns — the small set of work_orders DB columns
 * (manufacture seq, scheduled dates, design release, dispatch, install) we
 * surface in the Project Planner grid. The same query feeds both the admin
 * config page (include_inactive=true) and the planner itself
 * (include_inactive=false, the default).
 */
export function partnerStructuredColumnsQueryOptions(
  partnerId: string | undefined,
  options?: { includeInactive?: boolean },
) {
  const includeInactive = options?.includeInactive ?? false;
  return queryOptions({
    queryKey: [STRUCTURED_COLUMNS_KEY, 'partner', partnerId, { includeInactive }] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await structuredColumnsControllerGetPartnerColumns(
        {
          partner_id: partnerId,
          ...(includeInactive ? { include_inactive: 'true' } : {}),
        },
        { signal },
      );
      return expectData<PartnerStructuredColumnDto[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useTogglePartnerStructuredColumn(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: PartnerStructuredColumnToggleDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      await structuredColumnsControllerTogglePartnerColumn(dto);
    },
    onMutate: async (dto) => {
      // Optimistic update for both the admin (include_inactive=true) and
      // planner (include_inactive=false) views.
      const adminKey = [STRUCTURED_COLUMNS_KEY, 'partner', partnerId, { includeInactive: true }];
      const plannerKey = [STRUCTURED_COLUMNS_KEY, 'partner', partnerId, { includeInactive: false }];

      // Cancel BOTH keys so any in-flight refetch can't race-replace the
      // optimistic write we're about to make (or the rollback in onError).
      await Promise.all([
        queryClient.cancelQueries({ queryKey: adminKey }),
        queryClient.cancelQueries({ queryKey: plannerKey }),
      ]);

      const previousAdmin = queryClient.getQueryData<PartnerStructuredColumnDto[]>(adminKey);
      const previousPlanner = queryClient.getQueryData<PartnerStructuredColumnDto[]>(plannerKey);

      const applyOptimistic = (c: PartnerStructuredColumnDto): PartnerStructuredColumnDto => {
        if (c.key !== dto.structured_column_key) return c;
        // Always-on columns keep is_enabled=true regardless of what the caller
        // supplied. Toggleable columns reflect the requested state.
        const next: PartnerStructuredColumnDto = {
          ...c,
          is_enabled: c.is_always_on ? true : dto.is_enabled,
        };
        // ENG-568: when the caller supplies unit config, mirror it into the
        // cached row so the planner editor sees the new dropdown / default
        // without waiting for the onSettled refetch. Passing `null` clears
        // the partner override; resolve back to registry fallbacks for the
        // optimistic frame so the editor doesn't briefly render nothing.
        if (dto.default_unit !== undefined) {
          next.default_unit = dto.default_unit ?? c.allowed_units?.[0] ?? null;
        }
        if (dto.enabled_units !== undefined) {
          next.enabled_units = dto.enabled_units ?? c.allowed_units ?? null;
        }
        // Mirror date_format the same way — `null` means "clear" (planner
        // falls back to iso_8601 on render), an explicit key updates the
        // cell formatter immediately.
        if (dto.date_format !== undefined) {
          next.date_format = dto.date_format;
        }
        return next;
      };
      if (previousAdmin) {
        queryClient.setQueryData<PartnerStructuredColumnDto[]>(
          adminKey,
          previousAdmin.map(applyOptimistic),
        );
      }
      // Planner cache only holds enabled rows (include_inactive=false). For
      // toggle-OFF we drop the row; for unit-only edits that keep the column
      // enabled we mirror the new default/enabled units onto the existing
      // row so the planner editor picks them up immediately. Toggle-ON from
      // nothing still defers to the onSettled refetch (the registry shape
      // isn't visible here).
      if (previousPlanner) {
        if (!dto.is_enabled) {
          queryClient.setQueryData<PartnerStructuredColumnDto[]>(
            plannerKey,
            previousPlanner.filter((c) => c.key !== dto.structured_column_key),
          );
        } else if (previousPlanner.some((c) => c.key === dto.structured_column_key)) {
          queryClient.setQueryData<PartnerStructuredColumnDto[]>(
            plannerKey,
            previousPlanner.map(applyOptimistic),
          );
        }
      }
      return { previousAdmin, previousPlanner };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      const adminKey = [STRUCTURED_COLUMNS_KEY, 'partner', partnerId, { includeInactive: true }];
      const plannerKey = [STRUCTURED_COLUMNS_KEY, 'partner', partnerId, { includeInactive: false }];
      if (ctx.previousAdmin) queryClient.setQueryData(adminKey, ctx.previousAdmin);
      if (ctx.previousPlanner) queryClient.setQueryData(plannerKey, ctx.previousPlanner);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [STRUCTURED_COLUMNS_KEY, 'partner', partnerId],
      });
    },
  });
}
